import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Listbox } from "@headlessui/react";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components/macro";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";

import { FnrPopoverOptionsList } from "UI/FnrPopover2";
import { colors } from "UI/colors";
import { scrollbarDarkThin } from "UI/scrollbarStyles";

import { australiaStateOptions } from "Utilities/Address/australiaStateOptions";
import { canadaStateOptions } from "Utilities/Address/canadaStateOptions";
import { mexicoStateOptions } from "Utilities/Address/mexicoStateOptions";
import { peruStateOptions } from "Utilities/Address/peruStateOptions";
import { unitedStatesStateOptions } from "Utilities/Address/unitedStatesStateOptions";
import { useOnClickOutside } from "Utilities/Hooks/useOnClickOutside";

import { useAddress } from "./AddressContext";
import { Label } from "./Styles";

StateField.propTypes = {
  value: PropTypes.string,
  name: PropTypes.string,
  countryID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // country ID or country code abbreviation
  stateValue: PropTypes.string,
  onChange: PropTypes.func,
  onMenuChange: PropTypes.func,
  hideLabel: PropTypes.bool,
  buttonWidth: PropTypes.string,
  autoComplete: PropTypes.oneOf(["on", "off"]),
  style: PropTypes.object,
};

export function StateField({
  value,
  onChange,
  onMenuChange,
  countryID = "US",
  hideLabel,
  style,
  name = "state",
  autoComplete = "off",
  disabled,
}) {
  const { clearStateFieldTrigger } = useAddress();
  const [menuOpen, setMenuOpen] = useState(false);
  const stateButtonRef = useRef();
  const hiddenInputRef = useRef();
  const popoverRef = useRef();

  useOnClickOutside(popoverRef, (e) => {
    // close the state select popover if user clicks outside while menu is open
    if (menuOpen) setMenuOpen(false);
  });

  // This dismissed the popover if user pressed "escape" while menu was open. However, caused a bug where
  // if you opened menu with keyboard, then clicked button with mouse to close, it stayed open.
  const onFocus = (e) => {
    // console.log("onFocus e:", e);
    // if (menuOpen) setMenuOpen(false);
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  useEffect(() => {
    // This is how the state field clears when the country field is changed.
    if (clearStateFieldTrigger > 0) {
      onChange("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearStateFieldTrigger]);

  useEffect(() => {
    if (onMenuChange) onMenuChange(menuOpen);
  }, [menuOpen, onMenuChange]);

  useEffect(() => {
    return () => setMenuOpen(false);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      setMenuOpen(true);
    } else if (e.key === "Escape") {
      setMenuOpen(false);
    } else if (e.keyCode >= 65 && e.keyCode <= 90) {
      stateButtonRef.current.click();
    }
  };

  const handleSelect = (newValue) => {
    if (disabled) return;
    onChange(newValue.ID);
    setMenuOpen(false);
  };

  const handleAutoComplete = (e) => {
    let name = e.target.value;
    let stateID;
    let l = name.length;
    // depending on how the state is saved, it can be the full name or the abbreviation
    if (l === 2) {
      stateID = name.toUpperCase();
    } else {
      stateID = stateOptions.find(
        (s) => s.name.toLowerCase() === name.toLowerCase()
      )?.ID;
    }
    onChange(stateID);
  };

  const stateOptions = useMemo(() => {
    return countryID === 1 || countryID === "US"
      ? unitedStatesStateOptions
      : countryID === 2 || countryID === "CA"
      ? canadaStateOptions
      : countryID === 3 || countryID === "AU"
      ? australiaStateOptions
      : countryID === 9 || countryID === "MX"
      ? mexicoStateOptions
      : countryID === 10 || countryID === "PE"
      ? peruStateOptions
      : [];
  }, [countryID]);

  const has3LetterStateOptions = stateOptions.some(
    (option) => option?.ID.length === 3
  );

  const labelText = countryID === 2 ? "Prov" : "State";
  const selectedState = stateOptions.find((s) => s.ID === value);

  // only show the state field for US (1), CA (2), AU (3), MX (9), PE (10)
  const stateFieldCountryCodes = [1, 2, 3, 9, 10, "US", "CA", "AU", "MX", "PE"];
  if (!stateFieldCountryCodes.includes(countryID)) return null;

  return (
    <Wrapper data-cy="StateField_Container">
      <HiddenInput
        ref={hiddenInputRef}
        type="text"
        name={name}
        onChange={handleAutoComplete}
        tabIndex={-1}
        autoComplete={autoComplete}
      />
      <Listbox value={selectedState} onChange={handleSelect}>
        <>
          <Button
            ref={stateButtonRef}
            onClick={toggleMenu}
            onKeyDown={handleKeyDown}
            onFocus={onFocus}
            style={style}
            role="button"
            $isWide={has3LetterStateOptions}
          >
            <span>
              {hideLabel ? null : <StateLabel>{labelText}</StateLabel>}
              <StateValue value={value}>
                <span>{hideLabel && !value ? labelText : value} </span>
                <ChevronWrapper menuOpen={menuOpen}>
                  <FontAwesomeIcon icon={faChevronDown} />
                </ChevronWrapper>
              </StateValue>
            </span>
          </Button>

          <FnrPopoverOptionsList
            show={menuOpen}
            onShouldClose={toggleMenu}
            buttonRef={stateButtonRef}
          >
            <div ref={popoverRef}>
              <OptionList static>
                {stateOptions.map((state) => (
                  <Listbox.Option key={state.ID} value={state} role="option">
                    {({ selected, active }) => (
                      <OptionInner active={active} selected={selected}>
                        {state.name}
                        <CheckmarkHolder>
                          {selected ? <span className="fa fa-check" /> : null}
                        </CheckmarkHolder>
                      </OptionInner>
                    )}
                  </Listbox.Option>
                ))}
              </OptionList>
            </div>
          </FnrPopoverOptionsList>
        </>
      </Listbox>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  z-index: -1;
`;

const Button = styled(Listbox.Button)`
  background: transparent;
  border: none;
  border-right: 1px solid #e5e5e5;
  flex-shrink: 0;
  cursor: pointer;
  padding: 6px;
  width: ${({ $isWide }) => ($isWide ? "70px" : "60px")};
  transition: background-color 300ms;

  &:hover {
    background-color: #efefef;
  }

  &:focus {
    background-color: #efefef;
  }

  @media (max-width: 430px) {
    border-bottom: 1px solid #e5e5e5;
  }
`;

const StateValue = styled.div`
  display: flex;
  gap: 2px;
  justify-content: space-between;
  padding: 4px 0 4px 2px;
  color: ${(props) => (props.value ? "#222" : "rgb(117, 117, 117)")};
  font-weight: 400;
  font-size: 18px;
  font-family: "Open Sans", sans-serif;

  svg {
    padding-top: 6px;
    padding-left: 3px;
    color: ${colors.fnBlue};
    font-size: 14px;
  }
`;

const StateLabel = styled(Label)`
  text-align: left;
  cursor: pointer;
`;

const ChevronWrapper = styled.div`
  display: inline-block;
  width: 20px;
  transition: transform 200ms;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  transform-origin: 55% 60% 0;
  transform: ${({ menuOpen }) => (menuOpen ? "rotate(-180deg)" : null)};
`;

const OptionList = styled(Listbox.Options)`
  outline: none;
  max-height: 400px;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  padding-right: 6px;
  list-style: none;
  ${scrollbarDarkThin}
`;

const OptionInner = styled.div`
  color: black;
  padding: 6px 10px;
  color: white;
  opacity: ${({ selected }) => (selected ? 1 : 0.9)};
  background: ${({ active }) => (active ? "rgba(255, 255, 255, 0.15)" : null)};
  display: flex;
  border-radius: 10px;
  cursor: pointer;
  transition: background 150ms;
`;

const CheckmarkHolder = styled.div`
  width: 24px;
  flex-shrink: 0;
  flex-grow: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 4px;
`;
